/**
 * Últimos movimientos del sistema + historial completo con filtros.
 */
import { useCallback, useEffect, useState } from "react";
import { History, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { NativeDialog, NativeSelect } from "@/components/nativeUi";
import {
  AUDIT_ACTION_OPTIONS,
  AUDIT_ENTITY_OPTIONS,
  fetchAuditLogs,
  formatAuditDateTime,
} from "@/lib/auditApi";

function AuditMovementItem({ item }) {
  const actionEntity = [item.action_label, item.entity_type_label].filter(Boolean).join(" ");
  return (
    <li className="border border-gray-200 rounded-lg bg-white px-3 py-3 text-sm">
      <p className="text-xs text-gray-500 tabular-nums">{formatAuditDateTime(item.created_at)}</p>
      <p className="font-semibold text-gray-900 mt-0.5">{item.user_name || item.username || "Usuario"}</p>
      <p className="text-gray-800 mt-1">
        <span className="font-medium">{actionEntity}</span>
        {item.entity_name ? (
          <>
            {": "}
            <span className="text-gray-700">{item.entity_name}</span>
          </>
        ) : null}
      </p>
      {item.description ? <p className="text-gray-600 mt-1 text-xs leading-snug">{item.description}</p> : null}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700">
        <span className="font-medium">{item.member_delta_label || "sin cambio"}</span>
        {item.members_total_after != null ? (
          <span>
            Total después: <span className="font-semibold tabular-nums">{item.members_total_after}</span>
          </span>
        ) : null}
      </div>
    </li>
  );
}

const emptyFilters = {
  date_from: "",
  date_to: "",
  username: "",
  action: "",
  entity_type: "",
};

export function SystemAuditSection() {
  const [recent, setRecent] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [recentError, setRecentError] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [filters, setFilters] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historySkip, setHistorySkip] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const pageSize = 25;

  const loadRecent = useCallback(async () => {
    setLoadingRecent(true);
    setRecentError("");
    try {
      const data = await fetchAuditLogs({ limit: 10, skip: 0 });
      setRecent(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setRecentError(typeof detail === "string" ? detail : err?.message || "No se pudo cargar la auditoría.");
      setRecent([]);
    } finally {
      setLoadingRecent(false);
    }
  }, []);

  const loadHistory = useCallback(async (skip, filterValues) => {
    setLoadingHistory(true);
    setHistoryError("");
    try {
      const data = await fetchAuditLogs({
        limit: pageSize,
        skip,
        username: filterValues.username.trim() || undefined,
        action: filterValues.action || undefined,
        entity_type: filterValues.entity_type || undefined,
        date_from: filterValues.date_from || undefined,
        date_to: filterValues.date_to || undefined,
      });
      setHistoryItems(Array.isArray(data?.items) ? data.items : []);
      setHistoryTotal(typeof data?.total === "number" ? data.total : 0);
      setHistorySkip(skip);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setHistoryError(typeof detail === "string" ? detail : err?.message || "No se pudo cargar el historial.");
      setHistoryItems([]);
      setHistoryTotal(0);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    void loadRecent();
  }, [loadRecent]);

  const openHistory = () => {
    setHistoryOpen(true);
    setFilters(emptyFilters);
    setApplied(emptyFilters);
    void loadHistory(0, emptyFilters);
  };

  const applyFilters = (e) => {
    e?.preventDefault?.();
    setApplied({ ...filters });
    void loadHistory(0, filters);
  };

  const clearFilters = () => {
    setFilters(emptyFilters);
    setApplied(emptyFilters);
    void loadHistory(0, emptyFilters);
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg text-gray-800">
                <History className="w-5 h-5" />
                Últimos movimientos del sistema
              </CardTitle>
              <CardDescription className="mt-1">
                Registro persistente de altas, cambios, eliminaciones y restauraciones.
              </CardDescription>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => void loadRecent()} disabled={loadingRecent}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loadingRecent ? "animate-spin" : ""}`} />
              Actualizar
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {recentError ? <p className="text-sm text-red-600">{recentError}</p> : null}
          {loadingRecent ? (
            <p className="text-sm text-gray-500 py-4 text-center">Cargando movimientos…</p>
          ) : recent.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">Aún no hay movimientos registrados.</p>
          ) : (
            <ul className="space-y-2 max-h-[28rem] overflow-y-auto pr-1">
              {recent.map((item) => (
                <AuditMovementItem key={item.id || `${item.created_at}-${item.entity_id}`} item={item} />
              ))}
            </ul>
          )}
          <Button type="button" className="w-full sm:w-auto bg-slate-800 hover:bg-slate-900" onClick={openHistory}>
            Ver historial completo
          </Button>
        </CardContent>
      </Card>

      <NativeDialog open={historyOpen} onClose={() => setHistoryOpen(false)} className="w-full max-w-3xl p-6">
        <h2 className="text-xl font-bold text-gray-900 pr-8 mb-1">Historial completo de movimientos</h2>
        <p className="text-sm text-gray-600 mb-4">Filtre por fecha, usuario, acción o tipo de registro.</p>

        <form onSubmit={applyFilters} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className="space-y-1">
            <Label htmlFor="audit-date-from">Fecha desde</Label>
            <Input
              id="audit-date-from"
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="audit-date-to">Fecha hasta</Label>
            <Input
              id="audit-date-to"
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="audit-username">Usuario (nombre de usuario)</Label>
            <Input
              id="audit-username"
              value={filters.username}
              onChange={(e) => setFilters((f) => ({ ...f, username: e.target.value }))}
              placeholder="Ej. claudia"
              autoComplete="off"
            />
          </div>
          <div className="space-y-1">
            <Label>Tipo de acción</Label>
            <NativeSelect
              value={filters.action}
              onChange={(v) => setFilters((f) => ({ ...f, action: v }))}
            >
              {AUDIT_ACTION_OPTIONS.map((o) => (
                <option key={o.value || "all-a"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label>Tipo de registro</Label>
            <NativeSelect
              value={filters.entity_type}
              onChange={(v) => setFilters((f) => ({ ...f, entity_type: v }))}
            >
              {AUDIT_ENTITY_OPTIONS.map((o) => (
                <option key={o.value || "all-e"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="sm:col-span-2 flex flex-wrap gap-2">
            <Button type="submit" disabled={loadingHistory}>
              Aplicar filtros
            </Button>
            <Button type="button" variant="outline" onClick={clearFilters} disabled={loadingHistory}>
              Limpiar
            </Button>
          </div>
        </form>

        {historyError ? <p className="text-sm text-red-600 mb-2">{historyError}</p> : null}
        <p className="text-xs text-gray-500 mb-2">
          {historyTotal} {historyTotal === 1 ? "movimiento" : "movimientos"} encontrados
        </p>

        {loadingHistory ? (
          <p className="text-sm text-gray-500 py-6 text-center">Cargando historial…</p>
        ) : historyItems.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">No hay movimientos con estos filtros.</p>
        ) : (
          <ul className="space-y-2 max-h-[min(50vh,28rem)] overflow-y-auto pr-1 mb-4">
            {historyItems.map((item) => (
              <AuditMovementItem key={item.id || `${item.created_at}-${item.entity_id}`} item={item} />
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={loadingHistory || historySkip <= 0}
            onClick={() => void loadHistory(Math.max(0, historySkip - pageSize), applied)}
          >
            Anterior
          </Button>
          <span className="text-xs text-gray-600">
            Página {Math.floor(historySkip / pageSize) + 1} de{" "}
            {Math.max(1, Math.ceil(historyTotal / pageSize))}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={loadingHistory || historySkip + pageSize >= historyTotal}
            onClick={() => void loadHistory(historySkip + pageSize, applied)}
          >
            Siguiente
          </Button>
        </div>
      </NativeDialog>
    </>
  );
}
